<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { TuButton, TuInputCheckbox, TuInputText } from "@tinyurldev/tu-ui-vue";

import { logError } from "@Resources/js/helpers/logger";
import SanitizeDomainString from "@Modules/custom-domain/helpers/SanitizeDomainString";
import XOverlay from "@Modules/shared/store/XOverlay";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import { useTranslation } from "@Resources/js/composables";
import type { BrandedDomainCheckResult } from "@Modules/teams/Api/DomainsApi";
import PopoverOverhauling from "@Resources/js/components/popover/PopoverOverhauling.vue";

import CheckAddedDomain from "./CheckDomain.vue";

//
// Setup
const overlay = XOverlay();
const authStore = XAuthStore();
const { __ } = useTranslation();

//
// Props
interface Props {
  errors: Record<string, string>;
  loading: boolean;
  nameservers: string[];
  createDomain: (domain: string) => Promise<boolean>;
  checkDomain: (domain: string) => Promise<BrandedDomainCheckResult>;
}

const props = defineProps<Props>();

//
// Emit
interface Events {
  (e: "domain-created", domain: string): void;
}

const emit = defineEmits<Events>();

//
// Data
const confirmDomain = ref(false);
const domainFormVisible = ref(true);
const createdDomain = ref<{ domain: string } | null>(null);
const form = reactive({
  domain: "",
  busy: false,
  errors: {} as Record<string, string>,
});

//
// Computed
const domainValid = computed(() => /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9](?:\.[a-z]{2,})+$/i.test(form.domain));

//
// Methods
function cleanDomain(full = false): void {
  form.domain = SanitizeDomainString(form.domain, { removeTrailing: full });
}

function checkForm(): void {
  form.errors = {};

  if (!confirmDomain.value) {
    form.errors.check = __("confirm_domain");
  }

  cleanDomain(true);

  if (!domainValid.value) {
    form.errors.domain = __("invalid_domain");
  }
}

async function create(): Promise<void> {
  checkForm();

  if (Object.keys(form.errors).length > 0) {
    return;
  }

  form.busy = true;

  try {
    const success = await props.createDomain(form.domain);

    if (success) {
      createdDomain.value = { domain: form.domain };
      domainFormVisible.value = false;
      authStore.refreshApplicationState();
      emit("domain-created", form.domain);
    }
  } catch {
    logError(`[BYODomainPopover][create]: domain ${form.domain} could not be created.`);
  } finally {
    form.busy = false;
  }
}

function onDomainPaste(evt: ClipboardEvent): void {
  form.domain = evt.clipboardData?.getData("text") ?? "";
  checkForm();
}

function closeForm(): void {
  if (form.busy || props.loading) {
    return;
  }

  overlay.hide();
}

//
// Watchers
watch(() => form.domain, () => cleanDomain());

//
// Exposed Test Vars
defineExpose({ form, confirmDomain, domainFormVisible, createdDomain });
</script>

<template>
  <PopoverOverhauling @close="overlay.hide()">
    <template #title>
      {{ __("organization_domains_settings.byo_domain_popover.title") }}
    </template>

    <div class="byo-domain-popover">
      <template v-if="domainFormVisible">
        <h5 class="byo-domain-popover__intro text-muted">
          {{ __("organization_domains_settings.byo_domain_popover.subtitle") }}
        </h5>

        <p class="byo-domain-popover__description">
          {{ __("organization_domains_settings.byo_domain_popover.description_1") }}
        </p>
        <p class="byo-domain-popover__description">
          {{ __("organization_domains_settings.byo_domain_popover.description_2") }}
        </p>

        <form
          class="byo-domain-popover__form"
          @submit.prevent="create"
        >
          <TuInputCheckbox
            id="account_byod_checkbox_agree"
            v-model="confirmDomain"
            class="_confirm"
            :label="__('organization_domains_settings.byo_domain_popover.confirm_label')"
            :copy="{ errorMessage: form.errors.check }"
            @input="form.errors = {}"
          />

          <TuInputText
            id="domain"
            v-model="form.domain"
            class="_domain"
            :input="{ placeholder: 'yourdomain.com' }"
            :copy="{ help: __('organization_domains_settings.byo_domain_popover.domain_hint'), errorMessage: form.errors.domain || props.errors.domain }"
            @focus="form.errors = {}"
            @blur="checkForm()"
            @paste.prevent="onDomainPaste"
          />

          <div
            v-if="domainValid"
            class="byo-domain-popover__links-preview"
          >
            <small>
              {{ __("organization_domains_settings.byo_domain_popover.links_preview") }} <i>https://{{ form.domain }}/yourlink</i>
            </small>
          </div>

          <TuButton
            class="_create"
            type="submit"
            :label="__('organization_domains_settings.byo_domain_popover.create_button')"
            :is-loading="form.busy"
            :is-disabled="form.busy || props.loading"
            is-block
          />
          <TuButton
            class="_cancel"
            type="button"
            is-outlined
            :label="__('organization_domains_settings.byo_domain_popover.cancel_button')"
            :is-disabled="form.busy || props.loading"
            @click.stop="closeForm"
          />
        </form>
      </template>

      <template v-if="!domainFormVisible">
        <div class="byo-domain-popover__intro">
          <h5 class="text-muted">
            {{ __("organization_domains_settings.byo_domain_popover.subtitle") }}
          </h5>
          <h2 class="text-dark">
            {{ form.domain }}
          </h2>
        </div>

        <CheckAddedDomain
          :domain="createdDomain?.domain"
          :nameservers="nameservers"
          :check-domain="checkDomain"
        />

        <TuButton
          class="_finish"
          type="button"
          is-outlined
          :label="__('organization_domains_settings.byo_domain_popover.finish_button')"
          @click.stop="overlay.hide()"
        />
      </template>
    </div>
  </PopoverOverhauling>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.byo-domain-popover {
  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;

  &__form {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxs;
  }

  &__intro,
  &__description{
    margin: 0;

    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$base-line-height;
  }
}
</style>
